import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListTodo, Check, Circle, ArrowRight, AlertTriangle,
  ArrowUp, ArrowDown, Clock
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';

const PRIORITY_COLORS = {
  high: { dot: '#EF4444', bg: '#FEF2F2' },
  medium: { dot: '#F59E0B', bg: '#FFFBEB' },
  low: { dot: '#3B82F6', bg: '#EFF6FF' },
};

const STATUS_STYLES = {
  pending: { bg: '#FEF3C7', text: '#92400E', label: 'Pending' },
  in_progress: { bg: '#DBEAFE', text: '#1E40AF', label: 'In Progress' },
  complete: { bg: '#D1FAE5', text: '#065F46', label: 'Done' },
};

const TasksWidget = ({ maxItems = 5 }) => {
  const navigate = useNavigate();
  const { userTheme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await api.get('/todos');
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // Silently fail - widget is non-critical
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.post(`/todos/${id}/toggle`);
      setTasks(prev => prev.map(t => t._id === id ? res.data : t));
    } catch (err) {
      // Silently fail
    }
  };

  // Show active tasks first, then recently completed, limited to maxItems
  const activeTasks = tasks.filter(t => t.status !== 'complete');
  const completedTasks = tasks.filter(t => t.status === 'complete');
  const displayTasks = [...activeTasks, ...completedTasks].slice(0, maxItems);

  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'complete') return false;
    return new Date(task.due_date) < new Date(new Date().toDateString());
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (d >= today && d < tomorrow) return 'Today';
    if (d >= tomorrow && d < new Date(tomorrow.getTime() + 86400000)) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  };

  const overdueCount = activeTasks.filter(t => isOverdue(t)).length;
  const activeCount = activeTasks.length;

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <ListTodo className="w-4 h-4 text-violet-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800">My Tasks</h3>
        </div>
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-violet-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100" style={{ backgroundColor: userTheme?.dashboardWidgetHeader?.backgroundColor }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <ListTodo className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">My Tasks</h3>
            <p className="text-xs text-gray-400">
              {activeCount} active
              {overdueCount > 0 && (
                <span className="text-red-500 ml-1">· {overdueCount} overdue</span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/my-tasks')}
          className="flex items-center gap-1 text-xs font-medium text-violet-600 hover:text-violet-700 transition"
        >
          View All <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Task List */}
      {displayTasks.length === 0 ? (
        <div className="px-5 pb-5 pt-2 text-center">
          <p className="text-sm text-gray-400 py-4">No tasks yet</p>
          <button
            onClick={() => navigate('/admin/my-tasks')}
            className="text-xs font-medium text-violet-600 hover:text-violet-700"
          >
            + Add your first task
          </button>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {displayTasks.map(task => {
            const isComplete = task.status === 'complete';
            const overdue = isOverdue(task);
            const priorityColor = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
            const statusStyle = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
            const dueLabel = formatDate(task.due_date);

            return (
              <div
                key={task._id}
                className={`flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50/80 transition ${isComplete ? 'opacity-50' : ''}`}
              >
                {/* Toggle button */}
                <button
                  onClick={() => handleToggle(task._id)}
                  className="flex-shrink-0 transition-transform hover:scale-110"
                >
                  {isComplete ? (
                    <div className="w-4.5 h-4.5 w-[18px] h-[18px] rounded-full flex items-center justify-center bg-emerald-500">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <Circle className="w-[18px] h-[18px] text-gray-300 hover:text-gray-400" />
                  )}
                </button>

                {/* Priority dot */}
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: priorityColor.dot }}
                  title={`${task.priority} priority`}
                />

                {/* Title */}
                <span className={`flex-1 text-sm truncate ${isComplete ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                  {task.title}
                </span>

                {/* Due date */}
                {dueLabel && (
                  <span className={`text-[11px] flex-shrink-0 flex items-center gap-0.5 ${overdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                    {overdue && <AlertTriangle className="w-3 h-3" />}
                    {dueLabel}
                  </span>
                )}

                {/* Status badge */}
                <span
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                >
                  {statusStyle.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer - show if there are more tasks */}
      {tasks.length > maxItems && (
        <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={() => navigate('/admin/my-tasks')}
            className="text-xs text-gray-500 hover:text-violet-600 transition w-full text-center"
          >
            +{tasks.length - maxItems} more tasks
          </button>
        </div>
      )}
    </div>
  );
};

export default TasksWidget;
